import { api } from './api';
import { Call, CallFilters, CallResponse, CallStats } from '../types/call';
import { classifyRecordingFetchError, RecordingFetchError } from '../utils/recordingErrors';

export { RecordingFetchError };

export const callService = {
  // Get calls for a campaign
  async getCalls(campaignId: string, filters?: CallFilters): Promise<CallResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/campaigns/${campaignId}/calls?${params.toString()}`);
    return response.data.data;
  },

  // Get all calls for the user
  async getAllCalls(filters?: CallFilters): Promise<CallResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/calls/all?${params.toString()}`);
    return response.data.data;
  },

  // Get calls for a specific lead
  async getLeadCalls(leadId: string): Promise<Call[]> {
    const response = await api.get(`/calls/leads/${leadId}/calls`);
    return response.data.data.calls;
  },

  // Get a specific call
  async getCall(callId: string): Promise<Call> {
    const response = await api.get(`/calls/${callId}`);
    return response.data.data.call;
  },

  // Get call transcript
  async getCallTranscript(callId: string): Promise<string> {
    const response = await api.get(`/calls/${callId}/transcript`);
    return response.data.data.transcript;
  },

  // Get call recording URL (refreshes from Telnyx when stale)
  async getCallRecording(callId: string): Promise<string> {
    const response = await api.get(`/calls/${callId}/recording`);
    return response.data.data.recording_url;
  },

  /** Download recording file via authenticated stream (fresh URL from server). */
  async downloadCallRecording(callId: string, filenameBase?: string): Promise<void> {
    const response = await api.get(`/calls/${callId}/recording/stream`, {
      responseType: 'blob',
      timeout: 120000,
      skipErrorToast: true,
    });

    const blob = response.data as Blob;
    const type = blob?.type ?? '';

    if (type.includes('application/json')) {
      const text = await blob.text();
      try {
        const parsed = JSON.parse(text) as { error?: { message?: string } };
        throw new RecordingFetchError(
          (parsed.error?.message ?? '').toLowerCase().includes('not found')
            ? 'not_found'
            : 'unavailable',
          parsed.error?.message
        );
      } catch (e) {
        if (e instanceof RecordingFetchError) throw e;
        throw new RecordingFetchError('unavailable');
      }
    }

    if (!blob || blob.size === 0) {
      throw new RecordingFetchError('not_found');
    }

    const safeName = (filenameBase ?? 'call-recording')
      .trim()
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/-+/g, '-') || 'call-recording';
    const ext = type.includes('wav') ? 'wav' : 'mp3';
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeName}-${callId.slice(0, 8)}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /** Load recording as a blob URL for in-app playback (auth + CORS safe). Caller must revoke when done. */
  async getCallRecordingPlaybackUrl(callId: string): Promise<string> {
    try {
      const response = await api.get(`/calls/${callId}/recording/stream`, {
        responseType: 'blob',
        timeout: 120000,
        skipErrorToast: true,
      });

      const blob = response.data as Blob;
      const type = blob?.type ?? '';

      if (type.includes('application/json') || type.includes('text/plain')) {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text) as { error?: { message?: string } };
          const msg = parsed.error?.message ?? text;
          throw new RecordingFetchError(
            msg.toLowerCase().includes('not found') ? 'not_found' : 'unavailable',
            msg
          );
        } catch (e) {
          if (e instanceof RecordingFetchError) throw e;
          throw new RecordingFetchError('unavailable');
        }
      }

      if (!blob || blob.size === 0) {
        throw new RecordingFetchError('not_found');
      }

      return URL.createObjectURL(blob);
    } catch (error) {
      throw await classifyRecordingFetchError(error);
    }
  },

  // Get call stats for a campaign
  async getCallStats(campaignId: string): Promise<CallStats> {
    const response = await api.get(`/campaigns/${campaignId}/calls/stats`);
    return response.data.data.stats;
  },

  // Retry a failed call
  async retryCall(callId: string): Promise<Call> {
    const response = await api.post(`/calls/${callId}/retry`);
    return response.data;
  },

  // Cancel a pending call
  async cancelCall(callId: string): Promise<void> {
    await api.post(`/calls/${callId}/cancel`);
  },

  // Export call data
  async exportCalls(campaignId: string, filters?: CallFilters): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/calls/campaigns/${campaignId}/calls/export?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get call analytics
  async getCallAnalytics(campaignId: string, period: 'day' | 'week' | 'month' = 'week'): Promise<any> {
    const response = await api.get(`/calls/campaigns/${campaignId}/calls/analytics?period=${period}`);
    return response.data;
  }
};